/**
 * Embed configuration, read lazily from Vite env vars.
 *
 * Read lazily (via a function rather than a module-load constant) so that
 * tests can `vi.stubEnv(...)` before each call and get fresh values.
 */

export interface EmbedConfig {
  /** Whether the app runs in embedded (portal iframe) mode. */
  embedMode: boolean;
  /** Origin allowed to postMessage the embed; undefined disables the bridge. */
  allowedParentOrigin: string | undefined;
  /** Default brand accent colour applied on embed mount. */
  defaultAccent: string;
  /**
   * Whether to ask the host portal for an access token before falling back to
   * in-frame sign-in (MA-227). Default off: anything but the literal "true"
   * leaves the embed on today's silent-SSO-then-popup flow.
   */
  portalTokenEnabled: boolean;
  /**
   * The Entra RESOURCE the portal should mint a token for — `api://<client_id>`.
   *
   * Deliberately NOT `APPREG_SCOPE`. `VITE_APPREG_SCOPE` is
   * `api://<client_id>/<customer_name>`: that is the *scope*, the value MSAL
   * asks for. The portal's exchange takes a *resource*, and Entra sets the
   * minted token's `aud` to it — which is the claim `backend/auth/msal.py`
   * validates. Passing the scope form here would either be rejected by Entra or
   * produce a token the backend 401s, so the two must stay separate env vars
   * even though one is a prefix of the other.
   */
  portalTokenResource: string | undefined;
  /**
   * Tenant the portal should mint the token in.
   *
   * Its own var rather than parsed out of `VITE_APPREG_AUTHORITY`. Parsing the
   * trailing path segment works today, because the authority carries a tenant
   * GUID, and silently yields "organizations" or "common" the moment it does
   * not — i.e. a token request aimed at the wrong directory, with nothing
   * anywhere saying so. A var costs one line; fail closed when it is absent.
   */
  tenantId: string | undefined;
}

/**
 * Read a Vite env var, treating missing, empty, and unreplaced
 * `DUMMY_<key>` placeholder tokens as "not set" (undefined).
 */
function readEnv(key: string): string | undefined {
  const value = import.meta.env[key];
  if (value === undefined || value === "" || value === `DUMMY_${key}`) {
    return undefined;
  }
  return value;
}

export function getEmbedConfig(): EmbedConfig {
  return {
    embedMode: readEnv("VITE_EMBED_MODE") === "true",
    allowedParentOrigin: readEnv("VITE_PORTAL_ORIGIN"),
    defaultAccent: readEnv("VITE_EMBED_ACCENT") ?? "#f49600",
    portalTokenEnabled: readEnv("VITE_PORTAL_TOKEN_ENABLED") === "true",
    portalTokenResource: readEnv("VITE_PORTAL_TOKEN_RESOURCE"),
    tenantId: readEnv("VITE_APPREG_TENANT_ID"),
  };
}
